import clsx from 'clsx'
import Image from 'next/image'
import { ReactText, useState } from 'react'

/* blocks */
import Accordion from '@/atoms/Accordion/Accordion'

/* utils */
import { stripHtmlTags } from '@/utils/functions/utils'

/* styles */
import styles from './_.module.css'

interface FeatureDropdownProps {
  class?: string
  title: string
  description: string
  iconTagDetails?: {
    icon?: StrapiImageFile
    tag?: string
    color?: string
  }
  image?: StrapiImageFile
  list: {
    id: string | number
    title: string
    description: string
    bgColor?: string
    media?: StrapiImageFile
  }[]
  layout?: 'content-right' | 'content-left'
}

function FeatureDropdown({
  title,
  description,
  list,
  ...props
}: FeatureDropdownProps) {
  const firstFeature = list[0]
  const [activeKey, setActiveKey] = useState<ReactText | undefined>(list[0]?.id)
  const [activeMedia, setActiveMedia] = useState({ ...firstFeature })

  return (
    <section className="md:container | py-10 md:py-18">
      <div className="px-6 md:px-0 font-heading md:pb-16">
        {props.iconTagDetails ? (
          <div className="flex items-center | pb-8 lg:pb-4">
            {props.iconTagDetails.icon?.url ? (
              <Image
                src={props.iconTagDetails.icon.url}
                alt={props.iconTagDetails.icon.alternativeText ?? ''}
                width={props.iconTagDetails.icon.width}
                height={props.iconTagDetails.icon.height}
              />
            ) : null}
            <p
              className="uppercase text-xl font-semibold | ml-3"
              style={{ color: props.iconTagDetails.color }}
            >
              {props.iconTagDetails.tag}
            </p>
          </div>
        ) : null}
        {props.image?.url ? (
          <div className="flex items-center justify-between">
            <h2
              className="text-3xl md:text-5xl font-medium | md:max-w-col-8 mb-5 md:mb-0"
              dangerouslySetInnerHTML={{ __html: title }}
            />
            <Image
              src={props.image.url}
              alt={props.image.alternativeText ?? ''}
              width={props.image.width}
              height={props.image.height}
              className="hidden md:block"
            />
          </div>
        ) : (
          <h2
            className="text-3xl md:text-5xl font-medium | md:max-w-col-10 mb-5"
            dangerouslySetInnerHTML={{ __html: title }}
          />
        )}
        {stripHtmlTags(description) ? (
          <div
            className="text-xl md:text-2xl text-gray-60 | max-w-col-6 | mb-8"
            dangerouslySetInnerHTML={{ __html: description }}
          ></div>
        ) : null}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-12 gap-8 items-center">
        <div
          className={clsx(
            props.layout === 'content-right'
              ? 'order-2 md:col-start-7 md:col-end-13'
              : 'md:col-span-5 order-1',
            'flex flex-col gap-5 md:gap-8 | px-5 md:px-0',
            props.class
          )}
        >
          {list?.map(feature => {
            return (
              <Accordion
                key={feature.id}
                className={clsx(styles.accordion)}
                activeKey={activeKey}
                defaultOpen={feature.id === list[0].id}
                elementKey={feature.id}
                showIcon={false}
                title={feature.title}
                handleChange={key => {
                  setActiveKey(key)
                  setActiveMedia(feature ?? activeMedia)
                }}
              >
                <div
                  className="font-body text-base md:text-xl text-gray-60 | mb-4 md:mb-8 | "
                  dangerouslySetInnerHTML={{ __html: feature.description }}
                ></div>
              </Accordion>
            )
          })}
        </div>

        <div
          className={clsx(
            props.layout === 'content-right'
              ? 'md:col-span-5 md:order-1'
              : 'md:col-start-7 md:col-end-13 md:order-2',
            'md:rounded-2xl relative | m-auto'
          )}
          style={{ background: activeMedia.bgColor }}
        >
          {activeMedia ? (
            <Image
              className={clsx(styles.scheduleList, 'px-8 md:px-4 | mx-auto')}
              key={activeMedia.media?.url}
              src={activeMedia.media?.url ?? ''}
              width={activeMedia.media?.width}
              height={activeMedia.media?.height}
              alt={activeMedia.media?.alternativeText ?? ''}
            />
          ) : null}
        </div>
      </div>
    </section>
  )
}

export default FeatureDropdown
